import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
    Plus, FileText, VideoCamera, Lightbulb, MagnifyingGlass, Sparkle, ImageSquare, Rocket,
} from "@phosphor-icons/react";

const TYPE_ICON = { idea: Lightbulb, video: VideoCamera, written: FileText };
const TYPE_LABEL = { idea: "Idea", video: "Video", written: "Written" };

const empty = {
    title: "",
    source_type: "written",
    source_text: "",
    key_points: "",
    source_notes: "",
    audience: "",
    content_pillar: "",
    content_goal: "",
};

export default function SourcesTab() {
    const nav = useNavigate();
    const [rows, setRows] = useState([]);
    const [opts, setOpts] = useState({ source_types: [], audiences: [], content_pillars: [], content_goals: [] });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(null);   // "video" | "image" | null

    // V2.7 — Upload-as-Source. Drops the user straight into the
    // SourceDetail page for the freshly created source so they can
    // either hit "Ship to Buffer" or tweak inputs first.
    const uploadMedia = async (e, kind) => {
        const file = e.target?.files?.[0];
        if (!file) return;
        setUploading(kind);
        const t = toast.loading(
            kind === "video"
                ? "Transcribing video — this can take 30–60 seconds…"
                : "Uploading image…"
        );
        try {
            const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
            const token = localStorage.getItem("haevn:auth_token");
            const form = new FormData();
            form.append("file", file);
            const endpoint = kind === "video"
                ? "/content-studio/sources/upload-video"
                : "/content-studio/sources/upload-image";
            const resp = await fetch(apiBase + endpoint, {
                method: "POST",
                body: form,
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `Upload failed (${resp.status})`);
            }
            const data = await resp.json();
            toast.success(
                kind === "video" ? "Transcript captured" : "Image source created",
                {
                    id: t,
                    description: kind === "video"
                        ? `${(data.source_text || "").length} chars transcribed — opening Source…`
                        : "Opening Source…",
                },
            );
            nav(`/content-studio/sources/${data.id}`);
        } catch (err) {
            toast.error(err.message, { id: t });
        } finally {
            setUploading(null);
            e.target.value = "";
        }
    };

    const load = async () => {
        const params = {};
        if (search.trim()) params.search = search.trim();
        if (typeFilter !== "all") params.source_type = typeFilter;
        const [optR, listR] = await Promise.all([
            api.get("/content-studio/sources/options"),
            api.get("/content-studio/sources", { params }),
        ]);
        return { opts: optR.data, rows: listR.data || [] };
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await load();
                if (cancelled) return;
                setOpts(r.opts);
                setRows(r.rows);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, typeFilter]);

    const save = async () => {
        if (!form.title.trim()) { toast.error("Title required"); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            // strip empties so we don't hit enum validation
            Object.keys(payload).forEach(k => { if (payload[k] === "") delete payload[k]; });
            const r = await api.post("/content-studio/sources", payload);
            toast.success("Source created");
            setDialogOpen(false);
            setForm(empty);
            nav(`/content-studio/sources/${r.data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="sources-tab">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="font-display text-2xl font-bold leading-tight">Sources</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-[60ch]">
                        The upstream content. An Idea, a video transcript, an uploaded image, or a written piece becomes the source the AI transforms into platform-specific assets.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                <Button data-testid="new-source-btn" onClick={() => { setForm(empty); setDialogOpen(true); }} className="rounded-sm">
                    <Plus size={14} weight="bold" className="mr-1.5" /> New source
                </Button>
                <label className="cursor-pointer">
                    <input
                        type="file"
                        accept="video/*,audio/*,.mp4,.mov,.m4a,.mp3,.wav,.webm"
                        className="hidden"
                        onChange={(e) => uploadMedia(e, "video")}
                        data-testid="upload-video-input"
                        disabled={uploading}
                    />
                    <span className={`inline-flex items-center gap-1.5 rounded-sm border border-primary/40 text-primary px-3 h-9 text-[11px] font-mono uppercase tracking-wider hover:bg-primary/5 ${uploading === "video" ? "opacity-60" : ""}`}>
                        <VideoCamera size={13} weight="duotone" /> {uploading === "video" ? "Transcribing…" : "Upload video"}
                    </span>
                </label>
                <label className="cursor-pointer">
                    <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif"
                        className="hidden"
                        onChange={(e) => uploadMedia(e, "image")}
                        data-testid="upload-image-input"
                        disabled={uploading}
                    />
                    <span className={`inline-flex items-center gap-1.5 rounded-sm border border-primary/40 text-primary px-3 h-9 text-[11px] font-mono uppercase tracking-wider hover:bg-primary/5 ${uploading === "image" ? "opacity-60" : ""}`}>
                        <ImageSquare size={13} weight="duotone" /> {uploading === "image" ? "Uploading…" : "Upload image"}
                    </span>
                </label>
                </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                    <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        data-testid="sources-search"
                        placeholder="Search sources…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="rounded-sm h-9 pl-8 w-72"
                    />
                </div>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger data-testid="sources-type-filter" className="rounded-sm h-9 w-40 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All types</SelectItem>
                        {opts.source_types.map(t => (<SelectItem key={t} value={t}>{TYPE_LABEL[t] || t}</SelectItem>))}
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <p className="text-sm text-muted-foreground">Loading sources…</p>
            ) : rows.length === 0 ? (
                <div data-testid="sources-empty" className="border border-dashed border-border rounded-sm p-10 text-center">
                    <Sparkle size={20} weight="duotone" className="mx-auto text-primary mb-3" />
                    <p className="font-display text-base font-semibold">No sources yet</p>
                    <p className="text-sm text-muted-foreground mt-1 max-w-[44ch] mx-auto">
                        Create a new source, or open a Content Idea and click &ldquo;Send to Studio&rdquo; — that creates a Source seeded from the idea&apos;s hook, insight, and draft copy.
                    </p>
                </div>
            ) : (
                <div data-testid="sources-list" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {rows.map(s => {
                        const Icon = TYPE_ICON[s.source_type] || FileText;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                data-testid={`source-card-${s.id}`}
                                onClick={() => nav(`/content-studio/sources/${s.id}`)}
                                className="text-left border border-border bg-card p-5 rounded-sm hover-lift hover:border-primary/40 transition-colors"
                            >
                                <div className="flex items-center gap-2 mb-3">
                                    <Icon size={14} weight="duotone" className="text-primary" />
                                    <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{TYPE_LABEL[s.source_type] || s.source_type}</span>
                                    <span className="ml-auto font-mono text-[10px] tabular-nums text-muted-foreground">
                                        {s.asset_count || 0} asset{s.asset_count === 1 ? "" : "s"}
                                    </span>
                                </div>
                                <h3 className="font-display font-bold text-base leading-tight line-clamp-2">{s.title}</h3>
                                {s.source_text && (
                                    <p className="mt-3 text-xs text-muted-foreground line-clamp-3 leading-relaxed">{s.source_text}</p>
                                )}
                                <div className="flex flex-wrap gap-1.5 mt-3">
                                    {s.audience && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-muted rounded-sm">{s.audience}</span>}
                                    {s.content_pillar && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-muted rounded-sm">{s.content_pillar}</span>}
                                    {s.content_goal && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-primary/10 text-primary rounded-sm">{s.content_goal}</span>}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Create dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="rounded-sm max-w-2xl" data-testid="new-source-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display">New source</DialogTitle>
                        <DialogDescription className="text-[12px]">
                            Paste the source content — a transcript, blog draft, founder notes. The AI reads <strong>all</strong> of it when generating assets.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Title</label>
                            <Input data-testid="src-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Source type</label>
                                <Select value={form.source_type} onValueChange={v => setForm({ ...form, source_type: v })}>
                                    <SelectTrigger data-testid="src-type" className="rounded-sm h-9 mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {opts.source_types.map(t => (<SelectItem key={t} value={t}>{TYPE_LABEL[t] || t}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Content goal</label>
                                <Select value={form.content_goal || "none"} onValueChange={v => setForm({ ...form, content_goal: v === "none" ? "" : v })}>
                                    <SelectTrigger data-testid="src-goal" className="rounded-sm h-9 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">—</SelectItem>
                                        {opts.content_goals.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Audience</label>
                                <Select value={form.audience || "none"} onValueChange={v => setForm({ ...form, audience: v === "none" ? "" : v })}>
                                    <SelectTrigger data-testid="src-audience" className="rounded-sm h-9 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">—</SelectItem>
                                        {opts.audiences.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Content pillar</label>
                                <Select value={form.content_pillar || "none"} onValueChange={v => setForm({ ...form, content_pillar: v === "none" ? "" : v })}>
                                    <SelectTrigger data-testid="src-pillar" className="rounded-sm h-9 mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">—</SelectItem>
                                        {opts.content_pillars.map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Source text (transcript / body / notes)</label>
                            <Textarea
                                data-testid="src-text"
                                rows={8}
                                value={form.source_text}
                                onChange={e => setForm({ ...form, source_text: e.target.value })}
                                placeholder="Paste the transcript, the blog draft, or your founder notes here. Aim for full sentences so the AI has real context — not bullet fragments."
                                className="rounded-sm mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Key points (optional)</label>
                            <Textarea
                                data-testid="src-keypoints"
                                rows={2}
                                value={form.key_points}
                                onChange={e => setForm({ ...form, key_points: e.target.value })}
                                placeholder="A short summary of the most important takeaways. The AI uses this to anchor the generated assets."
                                className="rounded-sm mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                                Source notes <span className="text-[9px] text-accent normal-case tracking-normal italic">internal only — never quoted in output</span>
                            </label>
                            <Textarea
                                data-testid="src-notes"
                                rows={3}
                                value={form.source_notes}
                                onChange={e => setForm({ ...form, source_notes: e.target.value })}
                                placeholder="Direction for the AI that should inform the output but not appear in it. E.g. 'lean into the absurdity of low-effort dating' or 'avoid naming HAEVN directly'."
                                className="rounded-sm mt-1"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button data-testid="src-save" onClick={save} disabled={saving} className="rounded-sm">
                            {saving ? "Creating…" : "Create source"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
